import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { 
  ArrowLeft, Plus, Search, StickyNote, Pin, PinOff, Trash2, 
  Archive, MoreHorizontal, Tag, Bold, Italic, Underline as UnderlineIcon,
  Strikethrough, List, ListOrdered, CheckSquare, Link as LinkIcon,
  Highlighter, Code, Undo, Redo, FolderOpen, Table2, X, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import { getCurrentUserId } from '../utils/currentUser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_COLORS = [
  { value: '#FFFFFF', label: 'White' },
  { value: '#FEF3C7', label: 'Yellow' },
  { value: '#DBEAFE', label: 'Blue' },
  { value: '#DCFCE7', label: 'Green' },
  { value: '#FCE7F3', label: 'Pink' },
  { value: '#F3E8FF', label: 'Purple' },
  { value: '#FED7AA', label: 'Orange' },
];

const CURRENT_USER = {
  id: getCurrentUserId(),
  name: 'Principal Designer'
};

// Rich Text Editor Component
function RichTextEditor({ content, onChange, placeholder = "Start typing..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3',
      },
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-800">
      {/* Toolbar */}
      <div className="border-b p-2 flex flex-wrap gap-1 bg-gray-50 dark:bg-gray-700">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <UnderlineIcon className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <Strikethrough className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={editor.isActive('taskList') ? 'bg-gray-200 dark:bg-gray-600' : ''}
          title="To-Do List"
        >
          <CheckSquare className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={setLink}
          className={editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <LinkIcon className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={editor.isActive('highlight') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <Highlighter className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={editor.isActive('code') ? 'bg-gray-200 dark:bg-gray-600' : ''}
        >
          <Code className="w-4 h-4" />
        </Button>
        
        <div className="flex-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Editor */}
      <EditorContent editor={editor} />
      
      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          min-height: 200px;
          padding: 12px;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding: 0;
        }
        .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }
        .ProseMirror ul[data-type="taskList"] li > label {
          flex-shrink: 0;
          margin-top: 3px;
        }
        .ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div {
          text-decoration: line-through;
          color: #9ca3af;
        }
        .ProseMirror mark {
          background-color: #fef08a;
          border-radius: 2px;
          padding: 0 2px;
        }
        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
        }
        .ProseMirror code {
          background-color: #f3f4f6;
          border-radius: 4px;
          padding: 2px 4px;
          font-family: monospace;
          font-size: 0.9em;
        }
        .dark .ProseMirror {
          color: #e5e7eb;
        }
        .dark .ProseMirror code {
          background-color: #374151;
        }
        .dark .ProseMirror mark {
          background-color: #854d0e;
        }
      `}</style>
    </div>
  );
}

export default function NotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workTables, setWorkTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('keyMadeDarkMode') === 'true';
  });
  
  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setDarkMode(document.documentElement.classList.contains('dark'));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  // New/Edit note
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    color: '#FFFFFF',
    tags: [],
    project_id: '',
    work_table_id: '',
    content_type: 'rich'
  });
  const [newTag, setNewTag] = useState('');
  
  useEffect(() => {
    fetchNotes();
    fetchProjects();
    fetchWorkTables();
  }, [showArchived, filterProject]);
  
  const fetchNotes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('created_by', CURRENT_USER.id);
      params.append('is_archived', showArchived ? 'true' : 'false');
      if (searchQuery) params.append('search', searchQuery);
      if (filterProject) params.append('project_id', filterProject);
      
      const res = await axios.get(`${API}/notes?${params.toString()}`);
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };
  
  const fetchWorkTables = async () => {
    try {
      const res = await axios.get(`${API}/work-tables`, {
        params: { user_id: CURRENT_USER.id }
      });
      setWorkTables(res.data.work_tables || []);
    } catch (error) {
      console.error('Error fetching work tables:', error);
    }
  };
  
  const handleSearch = () => {
    fetchNotes();
  };
  
  const openNewNote = () => {
    setEditingNote(null);
    setNoteForm({ 
      title: '', 
      content: '', 
      color: '#FFFFFF', 
      tags: [],
      project_id: '',
      work_table_id: '',
      content_type: 'rich'
    });
    setShowNoteDialog(true);
  };
  
  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title || '',
      content: note.content || '',
      color: note.color || '#FFFFFF',
      tags: note.tags || [],
      project_id: note.project_id || '',
      work_table_id: note.work_table_id || '',
      content_type: note.content_type || 'rich'
    });
    setShowNoteDialog(true);
  };
  
  const saveNote = async () => {
    if (!noteForm.content.trim() && !noteForm.title.trim()) {
      toast.error('Note title or content is required');
      return;
    }
    
    try {
      const payload = {
        ...noteForm,
        project_id: noteForm.project_id || null,
        work_table_id: noteForm.work_table_id || null,
      };
      
      if (editingNote) {
        await axios.put(`${API}/notes/${editingNote.id}`, payload);
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/notes`, {
          ...payload,
          created_by: CURRENT_USER.id
        });
        toast.success('Note created');
      }
      setShowNoteDialog(false);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };
  
  const togglePin = async (note) => {
    try {
      await axios.put(`${API}/notes/${note.id}`, { is_pinned: !note.is_pinned });
      toast.success(note.is_pinned ? 'Unpinned' : 'Pinned');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };
  
  const archiveNote = async (noteId) => {
    try {
      await axios.put(`${API}/notes/${noteId}`, { is_archived: true });
      toast.success('Note archived');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to archive note');
    }
  };
  
  const unarchiveNote = async (noteId) => {
    try {
      await axios.put(`${API}/notes/${noteId}`, { is_archived: false });
      toast.success('Note restored');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to restore note');
    }
  };
  
  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note permanently?')) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };
  
  const addTag = () => {
    if (newTag.trim() && !noteForm.tags.includes(newTag.trim())) {
      setNoteForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };
  
  const removeTag = (tag) => {
    setNoteForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric'
    });
  };
  
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };
  
  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '';
  };
  
  const getWorkTableName = (workTableId) => {
    const wt = workTables.find(w => w.id === workTableId);
    return wt?.name || '';
  };
  
  // Separate pinned and unpinned notes
  const pinnedNotes = notes.filter(n => n.is_pinned);
  const unpinnedNotes = notes.filter(n => !n.is_pinned);
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-[#F8F7F5]'}`} data-testid="notes-page">
      {/* Header */}
      <header className={`${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-border'} border-b sticky top-0 z-50`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                My Desk
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-gray-600' : 'bg-border'}`} />
              <div className="flex items-center gap-2">
                <HelpIcon elementId="notes-page" position="inline" />
                <StickyNote className="w-5 h-5 text-accent" />
                <h1 className={`text-xl font-heading ${darkMode ? 'text-white' : 'text-foreground'}`}>Notes</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Project Filter */}
              <Select value={filterProject || '__all__'} onValueChange={(v) => setFilterProject(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-40">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9 w-64"
                />
              </div>
              
              {/* Toggle Archived */}
              <Button 
                variant={showArchived ? 'secondary' : 'outline'} 
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
              >
                <Archive className="w-4 h-4 mr-2" />
                {showArchived ? 'Archived' : 'Active'}
              </Button>
              
              {/* New Note */}
              <Button onClick={openNewNote} data-testid="new-note-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Note
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12">
            <StickyNote className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-muted-foreground'} opacity-50`} />
            <h2 className={`text-xl font-medium mb-2 ${darkMode ? 'text-white' : ''}`}>
              {showArchived ? 'No archived notes' : 'No notes yet'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {showArchived 
                ? 'Archived notes will appear here.'
                : 'Capture ideas, to-dos, and project notes with rich formatting.'}
            </p>
            {!showArchived && (
              <Button onClick={openNewNote}>
                <Plus className="w-4 h-4 mr-2" />
                Create your first note
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pinned Notes */}
            {pinnedNotes.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Pin className="w-4 h-4" />
                  Pinned
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pinnedNotes.map(note => (
                    <NoteCard 
                      key={note.id} 
                      note={note} 
                      darkMode={darkMode}
                      onEdit={openEditNote}
                      onTogglePin={togglePin}
                      onArchive={archiveNote}
                      onUnarchive={unarchiveNote}
                      onDelete={deleteNote}
                      formatDate={formatDate}
                      stripHtml={stripHtml}
                      getProjectName={getProjectName}
                      getWorkTableName={getWorkTableName}
                      showArchived={showArchived}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Other Notes */}
            {unpinnedNotes.length > 0 && (
              <div>
                {pinnedNotes.length > 0 && (
                  <h2 className="text-sm font-medium text-muted-foreground mb-3">
                    Others
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {unpinnedNotes.map(note => (
                    <NoteCard 
                      key={note.id} 
                      note={note} 
                      darkMode={darkMode}
                      onEdit={openEditNote}
                      onTogglePin={togglePin}
                      onArchive={archiveNote}
                      onUnarchive={unarchiveNote}
                      onDelete={deleteNote}
                      formatDate={formatDate}
                      stripHtml={stripHtml}
                      getProjectName={getProjectName}
                      getWorkTableName={getWorkTableName}
                      showArchived={showArchived}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={darkMode ? 'text-white' : ''}>
              {editingNote ? 'Edit Note' : 'New Note'}
            </DialogTitle>
            <DialogDescription>
              Create a note with rich text formatting, to-do lists, and link it to projects.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Note title..."
                className={`text-lg font-medium border-0 border-b rounded-none focus-visible:ring-0 px-0 ${darkMode ? 'bg-transparent text-white placeholder:text-gray-400' : ''}`}
                data-testid="note-title-input"
              />
            </div>
            
            {/* Rich Text Editor */}
            <div>
              <RichTextEditor
                content={noteForm.content}
                onChange={(html) => setNoteForm(prev => ({ ...prev, content: html }))}
                placeholder="Start typing... Use the toolbar for formatting, or type '[]' for a to-do list"
              />
            </div>
            
            {/* Associations Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={darkMode ? 'text-gray-300' : ''}>Link to Project</Label>
                <Select 
                  value={noteForm.project_id || '__none__'} 
                  onValueChange={(v) => setNoteForm(prev => ({ ...prev, project_id: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#2A2A2A] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className={darkMode ? 'text-gray-300' : ''}>Link to Work Table</Label>
                <Select 
                  value={noteForm.work_table_id || '__none__'} 
                  onValueChange={(v) => setNoteForm(prev => ({ ...prev, work_table_id: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#2A2A2A] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select work table..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {workTables.map(wt => (
                      <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Color & Tags Row */}
            <div className="flex gap-6">
              <div>
                <Label className={darkMode ? 'text-gray-300' : ''}>Color</Label>
                <div className="flex gap-2 mt-2">
                  {NOTE_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                        noteForm.color === color.value ? 'border-accent scale-110' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setNoteForm(prev => ({ ...prev, color: color.value }))}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex-1">
                <Label className={darkMode ? 'text-gray-300' : ''}>Tags</Label>
                <div className="flex gap-2 mt-2 flex-wrap items-center">
                  {noteForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <div className="flex gap-1">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="w-24 h-7 text-sm"
                    />
                    <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={addTag}>
                      <Tag className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveNote} data-testid="save-note-btn">
              {editingNote ? 'Update' : 'Create'} Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Note Card Component
function NoteCard({ 
  note, 
  darkMode,
  onEdit, 
  onTogglePin, 
  onArchive,
  onUnarchive,
  onDelete, 
  formatDate, 
  stripHtml,
  getProjectName,
  getWorkTableName,
  showArchived
}) {
  const projectName = note.project_id ? getProjectName(note.project_id) : '';
  const workTableName = note.work_table_id ? getWorkTableName(note.work_table_id) : '';
  
  return (
    <div
      className={`rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer group relative ${
        darkMode ? 'border border-gray-700' : ''
      }`}
      style={{ backgroundColor: darkMode && note.color === '#FFFFFF' ? '#374151' : note.color }}
      onClick={() => onEdit(note)}
      data-testid={`note-card-${note.id}`}
    >
      {/* Pin indicator */}
      {note.is_pinned && (
        <Pin className="absolute top-2 right-2 w-4 h-4 text-accent fill-accent" />
      )}
      
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 pr-8">
          {note.title && (
            <h3 className={`font-medium truncate ${darkMode && note.color === '#FFFFFF' ? 'text-white' : 'text-foreground'}`}>
              {note.title}
            </h3>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button 
              variant="ghost" 
              size="sm" 
              className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-8"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTogglePin(note); }}>
              {note.is_pinned ? (
                <>
                  <PinOff className="w-4 h-4 mr-2" />
                  Unpin
                </>
              ) : (
                <>
                  <Pin className="w-4 h-4 mr-2" />
                  Pin
                </>
              )}
            </DropdownMenuItem>
            {showArchived ? (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onUnarchive(note.id); }}>
                <Archive className="w-4 h-4 mr-2" />
                Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onArchive(note.id); }}>
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Content preview */}
      <p className={`text-sm line-clamp-4 mb-3 ${darkMode && note.color === '#FFFFFF' ? 'text-gray-300' : 'text-muted-foreground'}`}>
        {stripHtml(note.content)}
      </p>
      
      {/* Associations */}
      {(projectName || workTableName) && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {projectName && (
            <Badge variant="outline" className="text-xs gap-1">
              <FolderOpen className="w-3 h-3" />
              {projectName}
            </Badge>
          )}
          {workTableName && (
            <Badge variant="outline" className="text-xs gap-1">
              <Table2 className="w-3 h-3" />
              {workTableName}
            </Badge>
          )}
        </div>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 flex-wrap">
          {note.tags?.slice(0, 2).map(tag => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {note.tags?.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{note.tags.length - 2}
            </Badge>
          )}
        </div>
        <span className={`text-xs ${darkMode && note.color === '#FFFFFF' ? 'text-gray-400' : 'text-muted-foreground'}`}>
          {formatDate(note.updated_at || note.created_at)}
        </span>
      </div>
    </div>
  );
}
